import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { AliasType, ALIAS_TYPE_LABELS } from '@techgitter/contracts';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect } from '../dropdown';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { createCustomerAlias } from '../../services/customerAliasService';
import './reconciliationPanel.css';

// Alias-type choices for the SmartSelect — the numeric enum mapped to its
// labels; value is stringified (SmartSelect is string-valued) and parsed back.
const ALIAS_TYPE_OPTIONS = Object.values(AliasType)
  .filter((v): v is AliasType => typeof v === 'number')
  .map((t) => ({ value: String(t), label: ALIAS_TYPE_LABELS[t] }));

interface Props {
  open: boolean;
  /** Vendor the alias is scoped to (vendor-matching alias). */
  vendorId: string;
  vendorName?: string;
  /** The unmatched customer identifier from the import row → becomes the alias. */
  initialAlias: string;
  onClose: () => void;
  /**
   * Called after a successful create. `reconcile` reflects the "reconcile
   * affected records" checkbox; `identifier` is the unmatched vendor name (the
   * scope for that targeted reconcile). The parent refreshes the worklist either
   * way.
   */
  onCreated: (opts: { reconcile: boolean; identifier: string }) => void;
}

/**
 * Creates a customer alias for an unmatched import customer name — the second
 * reference-data create action on the reconciliation worklist. The operator
 * picks which customer the vendor's name refers to via the shared
 * CustomerLookupControl (the same async customer picker used by New Invoice /
 * New Ticket); the alias value is pre-filled from the import row.
 *
 * Presented as an on-system panel (the shared SidePanel shell) rendered inside a
 * chrome-stripped MUI Dialog so it keeps the centred overlay + backdrop the
 * worklist uses while looking like the rest of the app's panels.
 */
export const CreateAliasDialog: React.FC<Props> = ({
  open,
  vendorId,
  vendorName,
  initialAlias,
  onClose,
  onCreated,
}) => {
  const [selected, setSelected] = useState<CustomerIndexItemDto | null>(null);
  const [alias, setAlias] = useState(initialAlias);
  const [aliasType, setAliasType] = useState<AliasType>(AliasType.VendorMatching);
  const [reconcileAfter, setReconcileAfter] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!selected) {
      setError('Pick the customer this name belongs to.');
      return;
    }
    if (!alias.trim()) {
      setError('Alias is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createCustomerAlias(selected.dataverseCustomerId, {
        alias: alias.trim(),
        aliasType,
        // Vendor-matching aliases are scoped to the vendor; global/reference are not.
        vendorId: aliasType === AliasType.VendorMatching ? vendorId : null,
        isActive: true,
      });
      // Scope the reconcile by the raw unmatched name that's actually in the
      // import rows (initialAlias), not the editable alias field.
      onCreated({ reconcile: reconcileAfter, identifier: initialAlias });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create alias');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // No DialogTitle (the SidePanel header carries the title), so name the
      // dialog explicitly for assistive tech.
      aria-label="Create customer alias"
      // Strip the Dialog's own paper chrome — the SidePanel card provides the
      // visible surface (radius, shadow); the Dialog only supplies the centred
      // overlay, backdrop, focus trap, and Escape handling.
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          overflow: 'visible',
        },
      }}
    >
      <SidePanel
        title="Create customer alias"
        submitLabel={saving ? 'Creating…' : 'Create'}
        isValid={!saving && !!selected && !!alias.trim()}
        onClose={() => {
          if (!saving) onClose();
        }}
        onSubmit={submit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <p className="recon-dialog__hint">
          Map the {vendorName ?? 'vendor'} name “{initialAlias}” to one of our customers.
          The next reconcile will match import rows carrying this name.
        </p>

        {/* Same async customer picker as New Invoice / New Ticket. */}
        <CustomerLookupControl value={selected} onChange={setSelected} autoFocus />

        <FormField label="Alias (vendor's name for this customer)">
          <input value={alias} onChange={(e) => setAlias(e.target.value)} />
        </FormField>

        <SmartSelect
          label="Alias type"
          value={String(aliasType)}
          options={ALIAS_TYPE_OPTIONS}
          onChange={(v) => {
            if (v != null) setAliasType(Number(v) as AliasType);
          }}
        />

        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={reconcileAfter}
            onChange={(e) => setReconcileAfter(e.target.checked)}
          />
          Reconcile records with this new alias
        </label>
        <p className="recon-dialog__help">
          Re-evaluates only the import rows carrying this name — not the whole
          batch — so the matched rows update immediately.
        </p>
      </SidePanel>
    </Dialog>
  );
};
